import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Crown, Sparkles, Zap, ArrowRight,
  CreditCard, ExternalLink, Loader2, Star, Shield
} from "lucide-react";
import type { SubscriptionPlan } from "../../../../drizzle/schema";

// Mapa de features por plano para exibição no comparativo
const PLAN_FEATURES = [
  { label: "Agendamento Online", basic: true, professional: true, premium: true, elite: true },
  { label: "Caixa / Checkout", basic: true, professional: true, premium: true, elite: true },
  { label: "Profissionais", basic: "Até 3", professional: "Até 10", premium: "Até 20", elite: "Ilimitados" },
  { label: "Chatbot de IA", basic: false, professional: true, premium: true, elite: true },
  { label: "Pagamento Online (Stripe)", basic: false, professional: true, premium: true, elite: true },
  { label: "Gestão de Comissões", basic: false, professional: true, premium: true, elite: true },
  { label: "Notas de Voz", basic: false, professional: false, premium: true, elite: true },
  { label: "Relatórios Avançados", basic: false, professional: false, premium: true, elite: true },
  { label: "Painel de Chamada (TV)", basic: false, professional: false, premium: false, elite: true },
  { label: "Mídia Promocional (TV)", basic: false, professional: false, premium: false, elite: true },
  { label: "IA no WhatsApp", basic: false, professional: false, premium: false, elite: true },
  { label: "IA no Instagram", basic: false, professional: false, premium: false, elite: true },
  { label: "Sistema de Filiais", basic: false, professional: false, premium: false, elite: true },
];

const PLAN_ICONS: Record<string, React.ReactNode> = {
  basic: <Shield className="w-5 h-5" />,
  professional: <Zap className="w-5 h-5" />,
  premium: <Star className="w-5 h-5" />,
  elite: <Sparkles className="w-5 h-5" />,
};

const PLAN_COLORS: Record<string, string> = {
  basic: "border-border",
  professional: "border-primary/40",
  premium: "border-purple-500/40",
  elite: "border-yellow-500/60 shadow-lg shadow-yellow-500/10",
};

const PLAN_BADGE: Record<string, string | null> = {
  basic: null,
  professional: "Popular",
  premium: "Mais Vendido",
  elite: "Mais Completo",
};

function FeatureCell({ value }: { value: boolean | string }) {
  if (typeof value === "string") {
    return <span className="text-sm font-medium text-foreground">{value}</span>;
  }
  if (value) {
    return <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" />;
  }
  return <XCircle className="w-4 h-4 text-muted-foreground/40 mx-auto" />;
}

export default function AdminUpgrade() {
  const { user } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);

  const { data: plans, isLoading: plansLoading } = trpc.plans.list.useQuery();
  const { data: mySalon } = trpc.salon.getMySalon.useQuery(undefined, {
    enabled: !!user,
  });

  const createCheckout = trpc.salon.createSubscriptionCheckout.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        toast.info("Redirecionando para o pagamento...");
        window.open(data.url, "_blank");
      }
      setLoadingPlanId(null);
    },
    onError: (e) => {
      toast.error(e.message);
      setLoadingPlanId(null);
    },
  });

  const createBillingPortal = trpc.salon.createBillingPortal.useMutation({
    onSuccess: (data) => {
      if (data.url) window.open(data.url, "_blank");
    },
    onError: (e) => toast.error(e.message),
  });

  const handleUpgrade = (planId: number) => {
    setLoadingPlanId(planId);
    createCheckout.mutate({ planId, origin: window.location.origin });
  };

  const handleManageSubscription = () => {
    createBillingPortal.mutate({ origin: window.location.origin });
  };

  const currentPlanId = mySalon?.planId;
  const sortedPlans = (plans ?? [] as SubscriptionPlan[]).slice().sort((a: SubscriptionPlan, b: SubscriptionPlan) => Number(a.priceMonthly) - Number(b.priceMonthly));

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="flex justify-center">
          <div className="p-3 rounded-full bg-primary/10">
            <Crown className="w-8 h-8 text-primary" />
          </div>
        </div>
        <h1 className="text-3xl font-bold">Upgrade de Plano</h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Desbloqueie mais funcionalidades para o seu salão. Escolha o plano que melhor se adapta ao seu negócio.
        </p>
        {mySalon?.stripeCustomerId && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handleManageSubscription}
            disabled={createBillingPortal.isPending}
          >
            {createBillingPortal.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ExternalLink className="w-4 h-4" />
            )}
            Gerenciar Assinatura Atual
          </Button>
        )}
      </div>

      {/* Planos */}
      {plansLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {sortedPlans.map((plan: SubscriptionPlan) => {
            const isCurrent = plan.id === currentPlanId;
            const badge = PLAN_BADGE[plan.slug];
            const isElite = plan.slug === "elite";

            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col ${PLAN_COLORS[plan.slug] ?? "border-border"} ${isCurrent ? "ring-2 ring-primary" : ""}`}
              >
                {badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge
                      className={isElite
                        ? "bg-yellow-500 text-black hover:bg-yellow-500 text-xs"
                        : "bg-primary text-primary-foreground text-xs"
                      }
                    >
                      {badge}
                    </Badge>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-3">
                    <Badge variant="secondary" className="text-xs">Plano Atual</Badge>
                  </div>
                )}

                <CardHeader className="pb-3 pt-6">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${isElite ? "bg-yellow-500/20 text-yellow-500" : "bg-primary/10 text-primary"}`}>
                    {PLAN_ICONS[plan.slug] ?? <Crown className="w-5 h-5" />}
                  </div>
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <CardDescription className="text-xs">{plan.description ?? ""}</CardDescription>
                  <div className="pt-2">
                    <span className={`text-3xl font-bold ${isElite ? "text-yellow-500" : ""}`}>
                      R$ {Number(plan.priceMonthly).toFixed(0)}
                    </span>
                    <span className="text-muted-foreground text-sm">/mês</span>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col gap-3">
                  <Separator />
                  <div className="space-y-1.5 text-sm flex-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      <span>Até {plan.maxProfessionals === 999 ? "ilimitados" : plan.maxProfessionals} profissionais</span>
                    </div>
                    {plan.hasAiChatbot && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        <span>Chatbot de IA</span>
                      </div>
                    )}
                    {plan.hasOnlinePayment && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        <span>Pagamento Online</span>
                      </div>
                    )}
                    {plan.hasAdvancedReports && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        <span>Relatórios Avançados</span>
                      </div>
                    )}
                    {plan.hasAiWhatsapp && (
                      <div className="flex items-center gap-2 text-yellow-500">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>IA no WhatsApp</span>
                      </div>
                    )}
                    {plan.hasAiInstagram && (
                      <div className="flex items-center gap-2 text-yellow-500">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>IA no Instagram</span>
                      </div>
                    )}
                    {plan.hasPanelChamada && (
                      <div className="flex items-center gap-2 text-yellow-500">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>Painel de Chamada TV</span>
                      </div>
                    )}
                    {plan.slug === "elite" && (
                      <div className="flex items-center gap-2 text-yellow-500">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>Sistema de Filiais (múltiplas unidades)</span>
                      </div>
                    )}
                  </div>

                  {isCurrent ? (
                    <Button variant="secondary" className="w-full" disabled>
                      Plano Atual
                    </Button>
                  ) : !plan.stripePriceId ? (
                    <Button variant="outline" className="w-full" disabled>
                      Em breve
                    </Button>
                  ) : (
                    <Button
                      className={`w-full gap-2 ${isElite ? "bg-yellow-500 hover:bg-yellow-600 text-black" : ""}`}
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={loadingPlanId === plan.id}
                    >
                      {loadingPlanId === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Assinar
                          <ArrowRight className="w-3 h-3" />
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Tabela comparativa */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-center">Comparativo Completo</h2>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left p-3 font-medium text-muted-foreground w-48">Funcionalidade</th>
                  {sortedPlans.map((plan: SubscriptionPlan) => (
                    <th key={plan.id} className={`text-center p-3 font-semibold ${plan.id === currentPlanId ? "bg-primary/5 text-primary" : ""} ${plan.slug === "elite" ? "text-yellow-500" : ""}`}>
                    {plan.name}
                    {plan.id === currentPlanId && <div className="text-xs font-normal text-muted-foreground">Atual</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAN_FEATURES.map((feature, i) => (
                <tr key={i} className={`border-b last:border-0 ${i % 2 === 0 ? "bg-muted/10" : ""}`}>
                  <td className="p-3 font-medium">{feature.label}</td>
                  {sortedPlans.map((plan: SubscriptionPlan) => (
                    <td key={plan.id} className={`p-3 text-center ${plan.id === currentPlanId ? "bg-primary/5" : ""}`}>
                      <FeatureCell value={(feature as any)[plan.slug] ?? false} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Nota sobre teste */}
      <div className="text-center text-xs text-muted-foreground">
        <p>Para testar pagamentos, use o cartão <code className="bg-muted px-1 rounded">4242 4242 4242 4242</code> com qualquer data futura e CVV.</p>
        <p className="mt-1">Dúvidas? Entre em contato com o suporte.</p>
      </div>
    </div>
  );
}
